import { Alert, Card, Text, Title } from "@mantine/core";
import { IconBook, IconInfoCircle, IconQuote } from "@tabler/icons-react";

import React from "react";

export interface DyslexiaOverviewProps {
  percentile: number;
}

const DyslexiaOverview: React.FC<DyslexiaOverviewProps> = ({ percentile }) => {
  function getReadingAssessmentExplanation(percentile: number): {
    overview: string;
    interpretation: string;
    recommendation: string;
  } {
    const overview =
      "This test measures how the eyes move while reading and how quickly text is processed.";

    if (percentile >= 91) {
      return {
        overview,
        interpretation:
          "Scored in the top 10% for this age group. Reading speed and eye movements indicate strong reading skills.",
        recommendation: "Continue regular reading with more challenging material.",
      };
    } else if (percentile >= 76) {
      return {
        overview,
        interpretation:
          "Scored above average for this age group. Good reading fluency and smooth eye movements.",
        recommendation:
          "Continue regular reading practice. Gradually increasing text difficulty can help maintain progress.",
      };
    } else if (percentile >= 26) {
      return {
        overview,
        interpretation:
          "Scored at the average level for this age group. Reading patterns are typical.",
        recommendation: "Consistent daily reading practice supports continued skill development.",
      };
    } else if (percentile >= 11) {
      return {
        overview,
        interpretation:
          "Scored below average for this age group. Eye movements suggest more time may be needed to process text.",
        recommendation:
          "Additional reading practice may help. Consider following up with an appropriate specialist.",
      };
    } else {
      return {
        overview,
        interpretation:
          "Scored in the bottom 10% for this age group. Reading skills may benefit from extra support.",
        recommendation: "Consider following up with a reading specialist for further evaluation.",
      };
    }
  }

  const assessment = getReadingAssessmentExplanation(percentile);

  return (
    <Card withBorder radius="md" shadow="md" p="md" h="100%">
      <Title order={4} mb="sm">
        Reading Assessment Test
      </Title>
      <Alert variant="light" color="blue" mb={"xs"} icon={<IconQuote />}>
        <Text fz="sm">{assessment.overview}</Text>
      </Alert>

      <Alert variant="light" color="green" mb={"xs"} icon={<IconInfoCircle />}>
        <Text fz="sm">{assessment.interpretation}</Text>
      </Alert>

      <Alert variant="light" color="orange" icon={<IconBook />}>
        <Text fz="sm">{assessment.recommendation}</Text>
      </Alert>
    </Card>
  );
};

export default DyslexiaOverview;
